/**
 * Generic objective workspace list — /google-ads/<objective>.
 *
 * Reads the hierarchy live from Google (campaigns of this objective's channel
 * type → ad groups or asset groups → ads) and renders it as one scannable list
 * with a status pill and an enable switch per row. Every objective page
 * (leads, traffic, awareness, sales, local, shopping) is a thin config over
 * this component; YouTube and App promotion keep their older bespoke pages.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronRight, FileText, Globe, Info, Layers, Plus, RefreshCw, type LucideIcon } from 'lucide-react';
import {
  googleAdsService, GoogleAdsAdGroupRow, GoogleAdsAdRow, GoogleAdsAssetGroupRow, GoogleAdsCampaignSummary,
} from '@/services/google-ads.service';
import {
  adGroupAdResourceName, alreadyToasted, approvalOf, biddingLabel, CampaignDates, errMsg, formatMicros, newestFirst, Pill, pretty, Spinner,
  statusOf, Switch, Tag, useYoutubeAuth,
} from '@/components/google-ads/youtube/youtubeShared';

export interface WorkspaceExtras { leadFormCampaignIds?: Set<string> }

export interface ObjectiveWorkspaceConfig {
  key: string;
  title: string;
  subtitle: string;
  base: string;
  icon: LucideIcon;
  /** Google channel types that belong to this objective. */
  channelTypes: string[];
  newCampaign: string;
  /** PMAX has asset groups instead of ad groups + ads. */
  childKind: 'adgroup' | 'assetgroup';
  addChild?: (campaignId: string) => string;
  addAd?: (adGroupId: string) => string;
  /** Extra per-campaign tag (e.g. "Lead form"). */
  campaignTag?: (c: GoogleAdsCampaignSummary, extras: WorkspaceExtras) => React.ReactNode;
  loadExtras?: (accountUid: string) => Promise<WorkspaceExtras>;
  /** Sort lead-form campaigns first, etc. */
  sort?: (a: GoogleAdsCampaignSummary, b: GoogleAdsCampaignSummary, extras: WorkspaceExtras) => number;
  note?: string;
}

const isLive = (r: { id?: string | null; adId?: string | null }) => !!(r.id ?? r.adId);
const adName = (ad: GoogleAdsAdRow) => ad.name || ad.headlines[0] || ad.longHeadlines?.[0] || `Ad ${ad.adId}`;

function AdRows({ accountUid, customerId, adGroupId, addAd }: { accountUid: string; customerId: string; adGroupId: string; addAd?: string }) {
  const [ads, setAds] = useState<GoogleAdsAdRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setAds((await googleAdsService.listAds(accountUid, adGroupId)).filter(isLive)); } catch { setAds([]); }
  }, [accountUid, adGroupId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (ad: GoogleAdsAdRow) => {
    setBusy(ad.adId);
    const rn = adGroupAdResourceName(customerId, ad.adGroupId, ad.adId);
    try {
      if (ad.status === 'ENABLED') await googleAdsService.pauseAd(accountUid, rn); else await googleAdsService.resumeAd(accountUid, rn);
      load();
    } catch (e) { if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`); } finally { setBusy(null); }
  };

  return (
    <div className="pl-12">
      {ads === null ? <div className="flex items-center gap-1.5 py-2 text-xs text-text-muted"><Spinner size={12} />Loading ads…</div>
        : ads.length === 0 ? <div className="py-2 text-xs text-text-muted">No ads yet.</div>
        : ads.map((ad) => {
          const st = statusOf(ad.status);
          const approval = approvalOf(ad.approvalStatus, ad.reviewStatus);
          return (
            <div key={ad.adId} className="flex items-center gap-3 border-t border-border/40 py-2.5 first:border-t-0">
              <FileText size={14} className="shrink-0 text-text-muted" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium text-text-primary">{adName(ad)}</div>
                <div className="truncate text-2xs text-text-muted">
                  {ad.type ? pretty(ad.type) : 'Ad'}{ad.finalUrls[0] ? ` · ${ad.finalUrls[0]}` : ''}
                  {ad.headlines.length > 1 ? ` · ${ad.headlines.length} headlines` : ''}
                </div>
              </div>
              {approval && <Pill {...approval} className="hidden sm:inline-flex" />}
              <Pill {...st} />
              {ad.type === 'APP_AD'
                // Google refuses to pause app ads (AD_TYPE_CANNOT_BE_PAUSED) — they follow the campaign.
                ? <span className="text-2xs text-text-muted" title="App ads can't be paused on their own — pause the campaign">follows campaign</span>
                : <Switch checked={ad.status === 'ENABLED'} disabled={busy === ad.adId} label="Enable ad" onCheckedChange={() => toggle(ad)} />}
            </div>
          );
        })}
      {addAd && (
        <Link to={addAd} className="inline-flex items-center gap-1 py-2 text-2xs font-medium text-primary hover:underline"><Plus size={11} />Add ad</Link>
      )}
    </div>
  );
}

export function AdGroupRows({ accountUid, customerId, campaign, cfg }: {
  accountUid: string; customerId: string; campaign: GoogleAdsCampaignSummary; cfg: ObjectiveWorkspaceConfig;
}) {
  const [groups, setGroups] = useState<GoogleAdsAdGroupRow[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setGroups((await googleAdsService.listAdGroups(accountUid, campaign.id)).filter(isLive)); } catch { setGroups([]); }
  }, [accountUid, campaign.id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (g: GoogleAdsAdGroupRow) => {
    setBusy(g.id);
    try {
      if (g.status === 'ENABLED') await googleAdsService.pauseAdGroup(accountUid, g.id); else await googleAdsService.resumeAdGroup(accountUid, g.id);
      load();
    } catch (e) { if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`); } finally { setBusy(null); }
  };

  return (
    <div className="border-t border-border/60 bg-surface-alt/30 px-4 pb-2">
      {groups === null ? <div className="flex items-center gap-1.5 py-3 pl-8 text-xs text-text-muted"><Spinner size={12} />Loading ad groups…</div>
        : groups.length === 0 ? <div className="py-3 pl-8 text-xs text-text-muted">No ad groups yet.</div>
        : groups.map((g) => {
          const open = openId === g.id;
          const st = statusOf(g.status, g.primaryStatus);
          // Automated bidding leaves a placeholder bid (0.01) on the group — only meaningful under manual CPC.
          const bid = (campaign.biddingStrategyType ?? '').startsWith('MANUAL_CPC') ? formatMicros(g.cpcBidMicros, undefined) : null;
          return (
            <div key={g.id} className="border-t border-border/40 first:border-t-0">
              <div className="flex items-center gap-3 py-2.5 pl-8">
                <button type="button" onClick={() => setOpenId(open ? null : g.id)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                  {open ? <ChevronDown size={14} className="text-text-muted" /> : <ChevronRight size={14} className="text-text-muted" />}
                  <Layers size={14} className="shrink-0 text-text-muted" />
                  <div className="min-w-0">
                    <div className="truncate text-[13px] font-medium text-text-primary">{g.name}</div>
                    <div className="truncate text-2xs text-text-muted">{g.type ? pretty(g.type) : 'Ad group'}{bid ? ` · max CPC ${bid}` : ''}</div>
                  </div>
                </button>
                <Pill {...st} />
                <Switch checked={g.status === 'ENABLED'} disabled={busy === g.id} label="Enable ad group" onCheckedChange={() => toggle(g)} />
              </div>
              {open && <AdRows accountUid={accountUid} customerId={customerId} adGroupId={g.id} addAd={cfg.addAd?.(g.id)} />}
            </div>
          );
        })}
      {cfg.addChild && (
        <Link to={cfg.addChild(campaign.id)} className="inline-flex items-center gap-1 py-2 pl-8 text-2xs font-medium text-primary hover:underline"><Plus size={11} />Add ad group</Link>
      )}
    </div>
  );
}

function AssetGroupRows({ accountUid, campaign, cfg }: { accountUid: string; campaign: GoogleAdsCampaignSummary; cfg: ObjectiveWorkspaceConfig }) {
  const [groups, setGroups] = useState<GoogleAdsAssetGroupRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setGroups(await googleAdsService.performanceMax.listAssetGroups(accountUid, campaign.id)); } catch { setGroups([]); }
  }, [accountUid, campaign.id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (g: GoogleAdsAssetGroupRow) => {
    setBusy(g.id);
    try {
      if (g.status === 'ENABLED') await googleAdsService.performanceMax.pauseAssetGroup(accountUid, g.id);
      else await googleAdsService.performanceMax.resumeAssetGroup(accountUid, g.id);
      load();
    } catch (e) { if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`); } finally { setBusy(null); }
  };

  return (
    <div className="border-t border-border/60 bg-surface-alt/30 px-4 pb-2">
      {groups === null ? <div className="flex items-center gap-1.5 py-3 pl-8 text-xs text-text-muted"><Spinner size={12} />Loading asset groups…</div>
        : groups.length === 0 ? <div className="py-3 pl-8 text-xs text-text-muted">No asset groups yet — the campaign can't serve without one.</div>
        : groups.map((g) => {
          const st = statusOf(g.status, g.primaryStatus);
          return (
            <div key={g.id} className="flex items-center gap-3 border-t border-border/40 py-2.5 pl-8 first:border-t-0">
              <Layers size={14} className="shrink-0 text-text-muted" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium text-text-primary">{g.name}</div>
                <div className="truncate text-2xs text-text-muted">Asset group{g.finalUrls[0] ? ` · ${g.finalUrls[0]}` : ''}</div>
              </div>
              <Pill {...st} />
              <Switch checked={g.status === 'ENABLED'} disabled={busy === g.id} label="Enable asset group" onCheckedChange={() => toggle(g)} />
            </div>
          );
        })}
      {cfg.addChild && (
        <Link to={cfg.addChild(campaign.id)} className="inline-flex items-center gap-1 py-2 pl-8 text-2xs font-medium text-primary hover:underline"><Plus size={11} />Add asset group</Link>
      )}
    </div>
  );
}

export default function ObjectiveWorkspace({ cfg }: { cfg: ObjectiveWorkspaceConfig }) {
  const navigate = useNavigate();
  const { auth, accountUid } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);
  const currency = auth?.currency ?? undefined;

  const [rows, setRows] = useState<GoogleAdsCampaignSummary[] | null>(null);
  const [extras, setExtras] = useState<WorkspaceExtras>({});
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!accountUid) return;
    setError(null);
    try {
      const [list, ex] = await Promise.all([
        googleAdsService.listCampaigns(accountUid),
        cfg.loadExtras ? cfg.loadExtras(accountUid).catch(() => ({} as WorkspaceExtras)) : Promise.resolve({} as WorkspaceExtras),
      ]);
      setExtras(ex);
      setRows(list.filter((c) => cfg.channelTypes.includes(c.advertisingChannelType ?? '')));
    } catch (e) { setRows([]); setError(errMsg(e, 'Could not load campaigns.')); }
  }, [accountUid, cfg]);
  useEffect(() => { if (ready) load(); }, [ready, load]);

  const sorted = useMemo(() => {
    const list = newestFirst(rows ?? []);
    if (cfg.sort) list.sort((a, b) => cfg.sort!(a, b, extras));
    return list;
  }, [rows, cfg, extras]);

  const toggle = async (c: GoogleAdsCampaignSummary) => {
    const enable = c.status !== 'ENABLED';
    if (enable && !window.confirm(`Enable "${c.name}"? It will start spending its budget.`)) return;
    setBusy(c.id);
    try {
      if (enable) await googleAdsService.resumeCampaign(accountUid, c.id); else await googleAdsService.pauseCampaign(accountUid, c.id);
      toast.success(enable ? 'Campaign enabled' : 'Campaign paused');
      await load();
    } catch (e) { if (!alreadyToasted(e)) toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  const Icon = cfg.icon;
  return (
    <>
      <Topbar
        title={cfg.title}
        breadcrumb={`Google Ads / ${cfg.title}`}
        description={cfg.subtitle}
        right={ready ? (
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" iconLeft={<RefreshCw size={13} />} onClick={load}>Refresh</Button>
            <Button size="sm" iconLeft={<Plus size={13} />} onClick={() => navigate(cfg.newCampaign)}>New campaign</Button>
          </div>
        ) : undefined}
      />
      <div className="w-full px-7 py-6">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          {auth === undefined ? (
            <div className="flex items-center gap-2 py-10 text-sm text-text-muted"><Spinner />Checking Google Ads connection…</div>
          ) : !ready ? (
            <EmptyState
              icon={<Globe size={36} className="text-text-muted" />}
              title="Connect a Google Ads account first"
              description="Authorise Google and attach a customer ID, then come back to build campaigns."
              action={<Link to="/google-ads/account"><Button iconLeft={<Globe size={14} />}>Connect Google Ads</Button></Link>}
            />
          ) : (
            <>
              {cfg.note && (
                <div className="flex items-start gap-2.5 rounded-xl border border-info/30 bg-info-soft/40 px-4 py-3">
                  <Info size={14} className="mt-0.5 shrink-0 text-info" />
                  <p className="text-xs leading-relaxed text-text-secondary">{cfg.note}</p>
                </div>
              )}
              {error && <div className="rounded-xl border border-danger/30 bg-danger-soft/40 px-4 py-3 text-xs text-danger">{error}</div>}
              {rows === null ? (
                <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-16 rounded-2xl" />)}</div>
              ) : sorted.length === 0 ? (
                <EmptyState
                  icon={<Icon size={36} className="text-text-muted" />}
                  title={`No ${cfg.title.toLowerCase()} campaigns yet`}
                  description={cfg.subtitle}
                  action={<Button iconLeft={<Plus size={14} />} onClick={() => navigate(cfg.newCampaign)}>New campaign</Button>}
                />
              ) : (
                <Card padded={false} className="overflow-hidden">
                  {sorted.map((c) => {
                    const open = openId === c.id;
                    const st = statusOf(c.status, c.primaryStatus);
                    const daily = formatMicros(c.budgetAmountMicros, currency);
                    const total = !daily ? formatMicros(c.budgetTotalAmountMicros, currency) : null;
                    const bid = biddingLabel(c.biddingStrategyType);
                    return (
                      <div key={c.id} className="border-t border-border first:border-t-0">
                        <div className="flex items-center gap-3 px-4 py-3">
                          <button type="button" onClick={() => setOpenId(open ? null : c.id)} className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
                            {open ? <ChevronDown size={15} className="shrink-0 text-text-muted" /> : <ChevronRight size={15} className="shrink-0 text-text-muted" />}
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary"><Icon size={15} /></span>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="truncate text-sm font-semibold text-text-primary">{c.name}</span>
                                {cfg.campaignTag?.(c, extras)}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                <Tag>{pretty(c.advertisingChannelType)}</Tag>
                                {daily && <Tag>{daily} / day</Tag>}
                                {total && <Tag>{total} total</Tag>}
                                {bid && <Tag>{bid}</Tag>}
                                {c.startDate && <span className="text-2xs text-text-muted">{c.startDate}{c.endDate ? ` → ${c.endDate}` : ''}</span>}
                              </div>
                              <div className="mt-0.5"><CampaignDates c={c} /></div>
                            </div>
                          </button>
                          <Pill {...st} />
                          <Switch checked={c.status === 'ENABLED'} disabled={busy === c.id} label="Enable campaign" onCheckedChange={() => toggle(c)} />
                        </div>
                        {open && (cfg.childKind === 'assetgroup'
                          ? <AssetGroupRows accountUid={accountUid} campaign={c} cfg={cfg} />
                          : <AdGroupRows accountUid={accountUid} customerId={auth?.customer_id ?? ''} campaign={c} cfg={cfg} />)}
                      </div>
                    );
                  })}
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
